<template>
    <div
        class="mentions-pane-results__container"
        @mouseleave="onLeaveResults">
        <div
            class="mentions-pane-results__list">
            <div
                v-if="showLimitedResultsNotice"
                class="mentions-pane-results__notice">
                Showing first 100 results only
            </div>
            <use-virtual-list
                v-show="sortedItems.length > 0"
                ref="list"
                height="100%"
                :list="sortedItems"
                :options="{ itemHeight: 32 }">
                <template #default="{ index, data: item }">
                    <button
                        class="mentions-pane-results-item"
                        :class="{ focused, selected: index === selectedIndex }"
                        @click="onClickItem(item)"
                        @mouseenter="onHoverIndex(index)">
                        <pendo-icon
                            :type="getItemCategoryIcon(item.type)"
                            :size="16" />
                        <span class="mentions-pane-results-item__text">
                            {{ getItemText(item) }}
                        </span>
                        <span
                            v-if="showLabels"
                            class="mentions-pane-results-item__label">
                            {{ getItemCategoryLabel(item.type) }}
                        </span>
                    </button>
                </template>
            </use-virtual-list>
            <div
                v-show="sortedItems.length === 0"
                class="mentions-pane-results__empty">
                <pendo-loading-indicator
                    v-if="isLoading"
                    type="feather"
                    size="medium" />
                <pendo-empty-state
                    v-else-if="isResolved"
                    :title="$t('askPendo.mentionsResults.No results')"
                    :icon="{ type: getItemCategoryIcon(category) }" />
                <pendo-empty-state
                    v-else-if="isRejected"
                    :title="$t('askPendo.mentionsResults.Error')"
                    :description="$t('askPendo.mentionsResults.Unable to fetch results')"
                    :icon="{ type: 'x-circle' }" />
            </div>
            <div
                v-if="isInactiveToggleVisible"
                class="mentions-pane-results__toggle">
                Show inactive {{ category }}s
                <pendo-toggle
                    :value="inactiveToggleState"
                    @change="$emit('toggle-inactive', category)" />
            </div>
        </div>
        <div
            v-if="activeItem"
            class="mentions-pane-results__details">
            <account-visitor-card-content
                v-if="getItemDetailsType(activeItem.type) === 'accountvisitor'"
                :id="activeItem.id"
                ref="accountVisitorCardContent"
                :kind="activeItem.type"
                :name="getItemText(activeItem)" />
            <app-hover-card
                v-if="getItemDetailsType(activeItem.type) === 'app'"
                :app-id="`${activeItem.id}`"
                height="400px" />
            <countable-hover-card
                v-else-if="getItemDetailsType(activeItem.type) === 'countable'"
                :id="activeItem.id"
                :type="activeItem.type"
                height="400px" />
            <product-area-hover-card
                v-else-if="getItemDetailsType(activeItem.type) === 'productArea'"
                :group-id="activeItem.id"
                height="400px" />
            <segment-hover-card
                v-else-if="getItemDetailsType(activeItem.type) === 'segment'"
                :segment-id="activeItem.id"
                :segment-dependents="segmentDependents[activeItem.id]"
                height="400px" />
        </div>
    </div>
</template>

<script>
import { PendoEmptyState, PendoIcon, PendoLoadingIndicator, PendoToggle } from '@pendo/components';
import { UseVirtualList } from '@vueuse/components';
import SegmentHoverCard from '@/common/hover-card/segment-hover-card/segment-hover-card.vue';
import CountableHoverCard from '@/common/hover-card/countable-hover-card/countable-hover-card.vue';
import AppHoverCard from '@/common/hover-card/app-hover-card/app-hover-card.vue';
import ProductAreaHoverCard from '@/common/hover-card/product-area-hover-card/product-area-hover-card.vue';
import { mapState } from 'vuex';
import {
    getItemCategoryIcon,
    getItemCategoryLabel,
    getItemDetailsType,
    getItemText,
    itemSort,
} from '@/ask-pendo/components/mentions-popover/items.utils';
import AccountVisitorCardContent from '@/common/account-visitor-hover-card/account-visitor-card-content/account-visitor-card-content.vue';
import { LOADING_STATES } from '@/constants';

const { LOADING, REJECTED, RESOLVED } = LOADING_STATES;

export default {
    name: 'MentionsPaneResults',
    components: {
        AccountVisitorCardContent,
        AppHoverCard,
        CountableHoverCard,
        PendoEmptyState,
        PendoIcon,
        PendoLoadingIndicator,
        PendoToggle,
        ProductAreaHoverCard,
        SegmentHoverCard,
        UseVirtualList,
    },
    expose: ['onKeyDown'],
    props: {
        category: {
            type: String,
            required: true,
        },
        focused: {
            type: Boolean,
            required: true,
        },
        inactiveVisibleByCategory: {
            type: Object,
            required: true,
        },
        items: {
            type: Array,
            required: true,
        },
        loadingState: {
            type: String,
            required: true,
        },
        showLabels: {
            type: Boolean,
            required: true,
        },
        showLimitedResultsNotice: {
            type: Boolean,
            required: true,
        },
    },
    emits: ['focus', 'select', 'toggle-inactive'],
    data () {
        return {
            activeIndex: null, // index of item whose details are visible in details panel
            selectedIndex: null, // index of item holding the active selection, changeable via arrow keys
        };
    },
    computed: {
        ...mapState({
            segmentDependents: (state) => state.segments.segmentDependents,
        }),
        activeItem () {
            return this.activeIndex < this.sortedItems.length ? this.sortedItems[this.activeIndex] : null;
        },
        inactiveToggleState () {
            if (!this.isInactiveToggleVisible) {
                return false;
            }

            return this.inactiveVisibleByCategory[this.category];
        },
        isInactiveToggleVisible () {
            return this.category in this.inactiveVisibleByCategory;
        },
        isLoading () {
            return this.loadingState === LOADING;
        },
        isRejected () {
            return this.loadingState === REJECTED;
        },
        isResolved () {
            return this.loadingState === RESOLVED;
        },
        numItems () {
            return this.sortedItems.length;
        },
        selectedItem () {
            return this.selectedIndex < this.sortedItems.length ? this.sortedItems[this.selectedIndex] : null;
        },
        sortedItems () {
            return this.items.toSorted(itemSort);
        },
    },
    watch: {
        numItems () {
            this.setSelectedIndex(0);
        },
        selectedIndex (index) {
            this.scrollIndexIntoView(index);
        },
        activeItem: {
            async handler (item) {
                if (item && getItemDetailsType(item.type) === 'accountvisitor') {
                    await this.$nextTick();
                    this.$refs.accountVisitorCardContent?.setFetchingMetadata(false);
                    this.$refs.accountVisitorCardContent?.showHandle();
                }
            },
            immediate: true,
        },
    },
    mounted () {
        if (this.items.length) {
            this.setSelectedIndex(0);
        }
    },
    methods: {
        getItemCategoryLabel,
        getItemCategoryIcon,
        getItemDetailsType,
        getItemText,
        onClickItem (item) {
            this.$emit('select', item);
        },
        onHoverIndex (index) {
            this.activeIndex = index;
        },
        onKeyDown (event) {
            this.$emit('focus');

            if (event.key === 'ArrowDown') {
                this.selectDown();

                return true;
            }

            if (event.key === 'ArrowUp') {
                this.selectUp();

                return true;
            }

            if (event.key === 'Enter') {
                this.$emit('select', this.selectedItem);

                return true;
            }
        },
        onLeaveResults () {
            this.activeIndex = this.selectedIndex;
        },
        setSelectedIndex (index) {
            this.selectedIndex = index;
            this.activeIndex = index;
            this.scrollIndexIntoView(index);
        },
        scrollIndexIntoView (index) {
            this.$refs.list?.scrollTo(index);
        },
        selectDown () {
            if (this.sortedItems.length === 0) return;
            this.setSelectedIndex((this.selectedIndex + 1) % this.sortedItems.length);
        },
        selectUp () {
            if (this.sortedItems.length === 0) return;
            this.setSelectedIndex(this.selectedIndex === 0 ? this.sortedItems.length - 1 : this.selectedIndex - 1);
        },
    },
};
</script>

<style scoped lang="less">
.mentions-pane-results {
    &__container {
        display: flex;
        min-height: 200px;
    }

    &__list {
        width: 300px;

        display: flex;
        flex-direction: column;
        min-height: 400px;
        max-height: 400px;

        &:not(:has(.mentions-pane-results__notice)) {
            padding-top: 8px;
        }
    }

    &__notice {
        font-weight: bold;
        margin: 0;
        padding: 8px 16px;
        text-align: center;
    }

    &__empty {
        flex: 1;
        position: relative;
        width: 100%;

        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }

    &__toggle {
        display: flex;
        align-items: center;
        padding: 12px 8px;
        gap: 8px;
        border-top: 1px solid var(--palette-gray-30);
    }

    &__details {
        width: 320px;
        min-width: 320px;
        max-width: 320px;
        overflow: hidden;
        flex-shrink: 0;
        border-left: 1px solid var(--palette-gray-30);

        :deep(.hover-card) {
            background-color: unset;
            border: none;
            border-radius: unset;
        }
        :deep(.account-visitor-card-content) {
            height: 400px;
            padding: 16px;
            display: flex;
            flex-direction: column;
            justify-content: space-between;

            .account-visitor-card-content__metrics-container {
                display: flex;
                gap: 8px;
                flex-wrap: nowrap;
            }

            .account-visitor-card-content__metrics {
                flex: 1;
                min-width: 0; // prevents overflow
            }

            .account-visitor-card-content__metric {
                padding: 6px 4px;
            }

            .account-visitor-card-content__metric-value {
                font-size: 13px;
            }

            .account-visitor-card-content__metric-label {
                font-size: 10px;
                text-align: center;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
        }
    }
}

.mentions-pane-results-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    height: 32px;

    padding: 0 8px;
    background: none;
    border: none;

    line-height: 26px;

    &:hover {
        background-color: var(--palette-gray-20);
    }

    &.selected {
        background-color: var(--palette-gray-30);
    }

    &.selected.focused {
        color: var(--palette-gray-0);
        background-color: var(--palette-teal-70);
    }

    &__text {
        text-align: left;
        flex: 1;
        overflow: hidden;
        text-wrap: nowrap;
        text-overflow: ellipsis;
    }

    &__label {
        line-height: 26px;
        font-weight: 300;
        color: var(--palette-gray-70);
    }

    &.selected.focused &__label {
        color: var(--palette-gray-0);
    }
}
</style>